/**
 * Video area for voice channels and DM calls: cameras and screen shares in a grid, or one
 * tile in focus with the rest in a strip. In server voice channels, screen shares are only
 * downloaded once you click "Watch stream".
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Track, type Participant, type TrackPublication } from 'livekit-client';
import type { VoiceState } from '@comms/shared';
import { displayName, useStore } from '../store';
import { getRoom, setStreamVolume, stopScreenShare, useVoice, watchStream } from '../lib/voice';
import { GoLiveDialog } from './GoLive';
import { Avatar, Button, Icon, cx } from './ui';

interface Tile {
  key: string;
  identity: string;
  local: boolean;
  screen: boolean;
  /** null: a stream we aren't watching (yet) */
  pub: TrackPublication | null;
  label: string;
}

function collectTiles(guildId: string | null): Tile[] {
  const room = getRoom();
  if (!room) return [];
  const tiles: Tile[] = [];
  const participants: Participant[] = [room.localParticipant, ...room.remoteParticipants.values()];
  for (const p of participants) {
    const local = p === room.localParticipant;
    const name = displayName(useStore.getState(), p.identity, guildId);
    for (const pub of p.trackPublications.values()) {
      if (pub.kind !== Track.Kind.Video || pub.isMuted) continue;
      const screen = pub.source === Track.Source.ScreenShare;
      if (!screen && pub.source !== Track.Source.Camera) continue;
      const shown = pub.track ? pub : screen && !local && guildId ? null : undefined;
      if (shown === undefined) continue;
      tiles.push({
        key: `${p.identity}:${screen ? 'screen' : 'camera'}`,
        identity: p.identity,
        local,
        screen,
        pub: shown,
        label: screen ? `${local ? 'Your' : `${name}'s`} screen` : name,
      });
    }
  }
  // Screens first: they're what people gather to look at.
  return tiles.sort((a, b) => Number(b.screen) - Number(a.screen));
}

export function useHasVideo(guildId: string | null) {
  void useVoice((s) => s.tracksVersion);
  return collectTiles(guildId).length > 0;
}

export function VideoStage({
  guildId,
  states = [],
  className,
}: {
  guildId: string | null;
  /** voice states of this channel (server voice): who watches whom */
  states?: VoiceState[];
  className?: string;
}) {
  const voice = useVoice();
  const meId = useStore((s) => s.me?.id);
  const [focus, setFocus] = useState<string | null>(null);
  const [goLive, setGoLive] = useState(false);
  void voice.tracksVersion; // re-render on track changes
  const tiles = collectTiles(guildId);

  // Starting to watch a stream puts it in focus.
  useEffect(() => {
    if (voice.watching) setFocus(`${voice.watching}:screen`);
  }, [voice.watching]);

  const viewersOf = (identity: string) =>
    states.filter((s) => s.watchingUserId === identity).map((s) => s.userId);
  const focused = tiles.find((t) => t.key === focus) ?? null;
  const myViewers = meId ? viewersOf(meId) : [];

  const tileProps = (t: Tile) => ({
    tile: t,
    guildId,
    viewers: guildId && t.screen ? viewersOf(t.identity) : null,
    focused: focused?.key === t.key,
    onFocus: () => setFocus(focused?.key === t.key ? null : t.key),
  });

  if (tiles.length === 0 && !voice.screen) return null;
  return (
    <div className={cx('flex min-h-0 w-full flex-1 flex-col gap-3', className)}>
      {voice.screen && (
        <div
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-lg bg-danger/15 px-3 py-2 text-sm text-text-0"
        >
          <span className="rounded bg-danger px-1.5 text-xs font-bold text-white">LIVE</span>
          <span className="flex-1">
            You're live
            {guildId && (
              <span className="text-text-3">
                {' '}
                · {myViewers.length} watching
                {myViewers.length > 0 &&
                  `: ${myViewers.map((id) => displayName(useStore.getState(), id, guildId)).join(', ')}`}
              </span>
            )}
          </span>
          <Button variant="ghost" onClick={() => setGoLive(true)}>
            Change quality
          </Button>
          <Button variant="danger" onClick={() => void stopScreenShare()}>
            Stop streaming
          </Button>
          {goLive && <GoLiveDialog onClose={() => setGoLive(false)} />}
        </div>
      )}
      {focused ? (
        <>
          <div className="flex min-h-0 flex-1">
            <StageTile {...tileProps(focused)} />
          </div>
          {tiles.length > 1 && (
            <div className="flex h-28 shrink-0 gap-2 overflow-x-auto">
              {tiles
                .filter((t) => t.key !== focused.key)
                .map((t) => (
                  <div key={t.key} className="flex aspect-video h-full shrink-0">
                    <StageTile {...tileProps(t)} compact />
                  </div>
                ))}
            </div>
          )}
        </>
      ) : (
        tiles.length > 0 && (
          <div
            className={cx(
              'grid min-h-0 w-full flex-1 auto-rows-fr gap-3',
              tiles.length === 1
                ? 'grid-cols-1'
                : tiles.length <= 4
                  ? 'grid-cols-1 sm:grid-cols-2'
                  : 'grid-cols-2 md:grid-cols-3',
            )}
          >
            {tiles.map((t) => (
              <StageTile key={t.key} {...tileProps(t)} />
            ))}
          </div>
        )
      )}
    </div>
  );
}

type WebkitVideo = HTMLVideoElement & {
  webkitSupportsPresentationMode?: (mode: string) => boolean;
  webkitSetPresentationMode?: (mode: string) => void;
  webkitPresentationMode?: string;
};
let probe: WebkitVideo | undefined;
/** A detached video, to ask Safari whether picture in picture works before a tile has one. */
const webkitVideoProbe = () => (probe ??= document.createElement('video') as WebkitVideo);

function StageTile({
  tile,
  guildId,
  viewers,
  focused,
  onFocus,
  compact,
}: {
  tile: Tile;
  guildId: string | null;
  viewers: string[] | null;
  focused: boolean;
  onFocus: () => void;
  compact?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  // iPhone Safari can't put an element in fullscreen: the tile fills the screen instead.
  const [expanded, setExpanded] = useState(false);
  const watching = useVoice((s) => s.watching === tile.identity);
  const volume = useVoice((s) => s.streamVolume);
  const { pub } = tile;

  useEffect(() => {
    const track = pub?.track;
    const el = video.current;
    if (!track || !el) return;
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, [pub, pub?.track]);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setExpanded(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  const toggleFullscreen = () => {
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
      webkitExitFullscreen?: () => void;
    };
    const el = box.current as (HTMLDivElement & { webkitRequestFullscreen?: () => void }) | null;
    if (expanded) return setExpanded(false);
    if (doc.fullscreenElement) return void doc.exitFullscreen().catch(() => {});
    if (doc.webkitFullscreenElement) return doc.webkitExitFullscreen?.();
    if (el?.requestFullscreen && document.fullscreenEnabled)
      return void el.requestFullscreen().catch(() => setExpanded(true));
    if (el?.webkitRequestFullscreen) return el.webkitRequestFullscreen();
    setExpanded(true);
  };

  const v = video.current as WebkitVideo | null;
  const canPip =
    !!pub &&
    (document.pictureInPictureEnabled ||
      !!(v ?? webkitVideoProbe())?.webkitSupportsPresentationMode?.('picture-in-picture'));
  const pictureInPicture = () => {
    const el = video.current as WebkitVideo | null;
    if (!el) return;
    const webkit = () => {
      if (el.webkitSupportsPresentationMode?.('picture-in-picture'))
        el.webkitSetPresentationMode?.(
          el.webkitPresentationMode === 'picture-in-picture' ? 'inline' : 'picture-in-picture',
        );
    };
    if (document.pictureInPictureElement === el) void document.exitPictureInPicture();
    else if (document.pictureInPictureEnabled && el.requestPictureInPicture)
      void el.requestPictureInPicture().catch(webkit);
    else webkit();
  };

  const control = (title: string, icon: 'expand' | 'pip' | 'x', run: () => void): ReactNode => (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={(e) => (e.stopPropagation(), run())}
      className="rounded bg-black/60 p-1.5 text-white hover:bg-black/80"
    >
      <Icon name={icon} className="h-4 w-4" />
    </button>
  );

  return (
    <div
      ref={box}
      data-testid={tile.screen ? 'stream-tile' : 'camera-tile'}
      data-identity={tile.identity}
      onClick={pub ? onFocus : undefined}
      className={cx(
        'group min-h-0 w-full overflow-hidden bg-black',
        pub && 'cursor-pointer',
        expanded ? 'safe-y fixed inset-0 z-50' : 'relative rounded-lg',
        focused && !expanded && 'ring-2 ring-accent',
      )}
    >
      {pub ? (
        <video
          ref={video}
          autoPlay
          playsInline
          muted
          className={cx(
            'h-full w-full object-contain',
            tile.local && !tile.screen && '-scale-x-100',
          )}
        />
      ) : (
        <div className="flex h-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-bg-1 to-black p-3 text-center">
          <Avatar userId={tile.identity} size={compact ? 32 : 64} />
          <span className="rounded bg-danger px-1.5 text-xs font-bold text-white">LIVE</span>
          {!compact && (
            <span className="text-sm text-white">
              {displayName(useStore.getState(), tile.identity, guildId)} is streaming
            </span>
          )}
          <Button onClick={() => watchStream(tile.identity)}>Watch stream</Button>
        </div>
      )}
      <span className="absolute bottom-2 left-2 flex items-center gap-1 rounded bg-black/60 px-2 py-0.5 text-xs text-white">
        {tile.screen && pub && (
          <span className="rounded bg-danger px-1 text-[9px] font-bold">LIVE</span>
        )}
        {tile.label}
      </span>
      {viewers && viewers.length > 0 && (
        <span
          title={`Watching: ${viewers.map((id) => displayName(useStore.getState(), id, guildId)).join(', ')}`}
          className="absolute bottom-2 right-2 flex items-center gap-1 rounded bg-black/60 px-2 py-0.5 text-xs text-white"
        >
          <Icon name="eye" className="h-3 w-3" />
          {viewers.length} watching
          <span className="ml-1 flex -space-x-1.5">
            {viewers.slice(0, 3).map((id) => (
              <Avatar key={id} userId={id} size={16} />
            ))}
          </span>
        </span>
      )}
      {pub && (
        <div
          className={cx(
            'absolute right-2 top-2 items-center gap-1',
            focused || expanded ? 'flex' : 'hidden group-hover:flex',
          )}
        >
          {tile.screen && !tile.local && (
            <label
              className="flex items-center gap-1 rounded bg-black/60 px-1.5 py-1 text-white"
              onClick={(e) => e.stopPropagation()}
            >
              <Icon name="volume" className="h-4 w-4" />
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={volume}
                aria-label="Stream volume"
                onChange={(e) => setStreamVolume(Number(e.target.value))}
                className="w-20"
              />
            </label>
          )}
          {canPip && control('Picture in picture', 'pip', pictureInPicture)}
          {control(
            expanded ? 'Exit fullscreen' : 'Fullscreen',
            expanded ? 'x' : 'expand',
            toggleFullscreen,
          )}
          {watching && guildId && control('Stop watching', 'x', () => watchStream(null))}
        </div>
      )}
    </div>
  );
}
